import {
  DEFAULT_AVAILABLE_GOAL_CATEGORIES,
  DEFAULT_GOAL_SUGGESTIONS,
  DEFAULT_ROUTINE_SUGGESTION_PAYLOAD
} from 'assets/data';
import {
  APPLY_NATIVE_ACCESS_TOKEN_SOURCE,
  FLAG,
  LanguageCode
} from 'enums';
import { OK, PLATFORMS } from 'constants/general';
import {
  AppMetadataType,
  NotesAppMetaDataType,
  ToDoTaskAppMetaDataType
} from 'interfaces';
import { toast } from 'react-toastify';
import { applyNativeAccessToken } from 'bridge/native-auth.bridge';
import { changeLanguage, t } from 'services/i18n.service';
import {
  updateAvailableGoals,
  updateRoutineSuggestionPayload
} from 'store/reducer/routine-suggestions/slice';
import {
  updateFeedback,
  updateFlags,
  updateFont,
  updateLanguage,
  updatePlatform,
  updateThemeMode
} from 'store/reducer/setting/slice';
import { updateNotes } from 'store/reducer/note/slice';
import { updateBrainDump } from 'store/reducer/to-do/slice';
import { getAppDispatch, getAppState } from 'store/store-access';
import { getUniqueGoals } from 'utils/routine-suggestion.util';
import { mergeNotesPreferExisting } from 'utils/note.util';
import { sanitizeInput } from 'utils/todo.util';
import {
  exposeHasUserModifiedSettings,
  exposeloadSettingsData
} from 'bridge/user-settings.bridge';
import {
  exposeLoadIntentionForToDoPlayer,
  exposeUpdateBrainDumpFromDesktopApp,
  exposeStopToDoPlayer,
  exposeGetToDosFromPlayer,
  exposeLoadAccessTokenForToDo
} from 'bridge/todo-player.bridge';
import {
  exposeLoadFocusNames,
  exposeLoadTasksFocusSessionEnd
} from 'bridge/focus-session.bridge';
import {
  exposeRefreshWebview,
  exposeSyncWithDevices
} from 'bridge/sync-data.bridge';
import { exposeFbNavigate } from 'bridge/webview-navigation.bridge';
import {
  loadAccessTokenForChat,
  loadAccessTokenForCourse,
  loadAccessTokenForInspirationPage,
  loadAccessTokenForStats,
  loadAccessTokenForSurvey,
  loadMarketPageMetaData,
  loadMetaDataFoGetSupport,
  loadMetaDataForEnrolledCourses,
  loadMetaDataForRoutineSuggestion,
  loadMetaDataForSubscription,
  loadMetaDataForToDoPlayer
} from 'utils/focusBear_apps';

export const registerGlobalAppMetadataHandlers = () => {
  exposeHasUserModifiedSettings();
  exposeLoadIntentionForToDoPlayer();
  exposeLoadTasksFocusSessionEnd();
  exposeStopToDoPlayer();
  exposeUpdateBrainDumpFromDesktopApp();
  exposeRefreshWebview();
  exposeSyncWithDevices();
  exposeGetToDosFromPlayer();
  exposeStopToDoPlayer();
  exposeLoadAccessTokenForToDo();
  exposeloadSettingsData();
  exposeLoadFocusNames();
  exposeFbNavigate();

  /* deprecated */
  loadMetaDataForRoutineSuggestion();
  loadMetaDataFoGetSupport();
  loadAccessTokenForStats();
  loadAccessTokenForInspirationPage();
  loadMetaDataForSubscription();
  loadMetaDataForToDoPlayer();

  loadAccessTokenForChat();
  loadAccessTokenForSurvey();
  loadMetaDataForEnrolledCourses();
  loadAccessTokenForCourse();
  loadMarketPageMetaData();
  window.initAppMetaData = async (
    data: AppMetadataType | ToDoTaskAppMetaDataType | NotesAppMetaDataType
  ) => {
    return processAppMetaData(data);
  };
};

export const processAppMetaData = async (
  data: AppMetadataType | ToDoTaskAppMetaDataType | NotesAppMetaDataType
) => {
  let isProcessing = false;

  const access_token = data?.access_token || null;

  if (isProcessing) {
    console.warn('initAppMetaData already processing, skipping duplicate call');
    return OK;
  }

  isProcessing = true;

  try {
    const metaData = {
      ...data,
      flags: [PLATFORMS.ANDROID, PLATFORMS.IOS, PLATFORMS.MOB].includes(
        data.platform ?? ''
      )
        ? [
            ...(data?.flags ?? []).filter((flag) => flag !== FLAG.GEEK_MODE),
            FLAG.SIMPLE_MODE
          ]
        : data.flags
    };

    const actions = [];
    const langCode =
      metaData.lang === (LanguageCode.ES as string)
        ? LanguageCode.ES
        : LanguageCode.EN;
    changeLanguage(langCode);

    let flags = (metaData.flags ?? []) as FLAG[];
    if (flags?.length) {
      if (
        !flags.includes(FLAG.SIMPLE_MODE) &&
        !flags.includes(FLAG.GEEK_MODE)
      ) {
        flags = [...flags, FLAG.GEEK_MODE];
      }
    } else {
      flags = [FLAG.GEEK_MODE];
    }

    actions.push(
      ...[
        updateFlags(flags),
        updateLanguage(langCode),
        updateThemeMode(metaData.theme),
        updateFont(metaData.font),
        updatePlatform(metaData?.platform)
      ]
    );

    if (metaData?.feedback) {
      actions.push(updateFeedback(metaData.feedback));
    }

    const brainDumpRaw =
      'brain_dump' in data ? data.brain_dump : undefined;
    if (brainDumpRaw) {
      const sanitizedBrainDump = sanitizeInput(brainDumpRaw);
      if (sanitizedBrainDump.trim().length > 0) {
        actions.push(updateBrainDump(sanitizedBrainDump));
      }
    }

    const seededNotes = 'notes' in data ? data.notes : undefined;
    if (Array.isArray(seededNotes) && seededNotes.length > 0) {
      const existingNotes = getAppState()?.note.notes ?? [];
      actions.push(
        updateNotes(mergeNotesPreferExisting(seededNotes, existingNotes))
      );
    }

    const [RAW_GOAL_BUILD_HEALTHY_HABITS, RAW_GOAL_STAY_FOCUSED_AT_WORK] = [
      'Build healthy habits',
      'Stay focused at work'
    ];
    const [buildHealthyHabits, stayFocusedAtWork] = DEFAULT_GOAL_SUGGESTIONS;
    const defaultGoals = [buildHealthyHabits, stayFocusedAtWork];
    const rawGoals = data?.long_term_goals?.filter(Boolean) || [];

    const normalizedGoals = rawGoals.map((goal) => {
      if (goal.includes(RAW_GOAL_BUILD_HEALTHY_HABITS)) {
        return buildHealthyHabits;
      }
      if (goal.includes(RAW_GOAL_STAY_FOCUSED_AT_WORK)) {
        return stayFocusedAtWork;
      }
      return goal;
    });

    const user_goals = [
      ...defaultGoals.filter((goal) => !normalizedGoals.includes(goal)),
      ...normalizedGoals
    ];

    actions.push(
      updateRoutineSuggestionPayload({
        ...DEFAULT_ROUTINE_SUGGESTION_PAYLOAD,
        user_goals: getUniqueGoals(user_goals)
      })
    );

    const updatedAvailGoals = getUniqueGoals(
      DEFAULT_GOAL_SUGGESTIONS.concat(user_goals)
    );
    actions.push(
      updateAvailableGoals({
        ...DEFAULT_AVAILABLE_GOAL_CATEGORIES,
        [t('routine_suggestion.category_other')]: updatedAvailGoals
      })
    );

    if (access_token) {
      void applyNativeAccessToken(access_token, {
        source: APPLY_NATIVE_ACCESS_TOKEN_SOURCE.INIT_APP_META_DATA,
        requestId: data.requestId
      });
    } else {
      toast.warning(t('auth_page.token_missing_warning'));
    }

    actions.forEach((action) => getAppDispatch()?.(action));

    return OK;
  } finally {
    isProcessing = false;
  }
};

export { sendDataToPlatform } from 'bridge/sync-data.bridge';
